import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import EmissionFactorsPage from "./pages/EmissionFactorsPage";
import EmissionFactorDetailPage from "./pages/EmissionFactorDetailPage";
import EmissionFactorFormPage from "./pages/EmissionFactorFormPage";
import BulkEmissionFactorPage from "./pages/BulkEmissionFactorPage";
import ResubmitEmissionFactorPage from "./pages/ResubmitEmissionFactorPage";
import PendingApprovalsPage from "./pages/PendingApprovalsPage";
import AuditLogsPage from "./pages/AuditLogsPage";
import UserManagementPage from "./pages/UserManagementPage";
import CustomMappingsPage from "./pages/CustomMappingsPage";
import CustomMappingFormPage from "./pages/CustomMappingFormPage";
import BulkCustomMappingPage from "./pages/BulkCustomMappingPage";
import CustomMappingDetailPage from "./pages/CustomMappingDetailPage";
import CustomMappingEditPage from "./pages/CustomMappingEditPage";
import CategoryConversionsPage from "./pages/CategoryConversionsPage";
import CategoryConversionFormPage from "./pages/CategoryConversionFormPage";
import CategoryConversionDetailPage from "./pages/CategoryConversionDetailPage";
import ConversionGraphPage from "./pages/ConversionGraphPage";
import UnitConversionsPage from "./pages/UnitConversionsPage";
import UnitConversionFormPage from "./pages/UnitConversionFormPage";
import UnitConversionDetailPage from "./pages/UnitConversionDetailPage";
import LcaImpactFactorsPage from "./pages/LcaImpactFactorsPage";
import LcaImpactFactorDetailPage from "./pages/LcaImpactFactorDetailPage";
import EsgReportExtractionPage from "./pages/EsgReportExtractionPage";

// Protected Route Component
const ProtectedRoute = ({ children, roles }) => {
  const { user, loading, hasRole } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (roles && !hasRole(roles)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route Component (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />

      {/* Protected routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/emission-factors" element={<ProtectedRoute><EmissionFactorsPage /></ProtectedRoute>} />
      <Route path="/emission-factors/new" element={<ProtectedRoute roles={['admin', 'editor', 'approver']}><EmissionFactorFormPage /></ProtectedRoute>} />
      <Route path="/emission-factors/bulk" element={<ProtectedRoute roles={['admin', 'editor', 'approver']}><BulkEmissionFactorPage /></ProtectedRoute>} />
      <Route path="/emission-factors/:id/edit" element={<ProtectedRoute roles={['admin', 'editor', 'approver']}><EmissionFactorFormPage /></ProtectedRoute>} />
      <Route path="/emission-factors/:id" element={<ProtectedRoute><EmissionFactorDetailPage /></ProtectedRoute>} />
      <Route path="/emission-factors/:id/resubmit" element={<ProtectedRoute><ResubmitEmissionFactorPage /></ProtectedRoute>} />
      <Route path="/pending-approvals" element={<ProtectedRoute roles={['admin', 'approver']}><PendingApprovalsPage /></ProtectedRoute>} />
      <Route path="/custom-mappings" element={<ProtectedRoute><CustomMappingsPage /></ProtectedRoute>} />
      <Route path="/custom-mappings/new" element={<ProtectedRoute roles={['admin', 'editor', 'approver']}><CustomMappingFormPage /></ProtectedRoute>} />
      <Route path="/custom-mappings/bulk" element={<ProtectedRoute roles={['admin', 'editor', 'approver']}><BulkCustomMappingPage /></ProtectedRoute>} />
      <Route path="/custom-mappings/:id/edit" element={<ProtectedRoute roles={['admin', 'editor', 'approver']}><CustomMappingEditPage /></ProtectedRoute>} />
      <Route path="/custom-mappings/:id" element={<ProtectedRoute><CustomMappingDetailPage /></ProtectedRoute>} />
      <Route path="/category-conversions" element={<ProtectedRoute><CategoryConversionsPage /></ProtectedRoute>} />
      <Route path="/category-conversions/graph" element={<ProtectedRoute><ConversionGraphPage /></ProtectedRoute>} />
      <Route path="/category-conversions/new" element={<ProtectedRoute roles={['admin', 'editor', 'approver']}><CategoryConversionFormPage /></ProtectedRoute>} />
      <Route path="/category-conversions/:id/edit" element={<ProtectedRoute roles={['admin', 'editor', 'approver']}><CategoryConversionFormPage /></ProtectedRoute>} />
      <Route path="/category-conversions/:id" element={<ProtectedRoute><CategoryConversionDetailPage /></ProtectedRoute>} />
      <Route path="/unit-conversions" element={<ProtectedRoute><UnitConversionsPage /></ProtectedRoute>} />
      <Route path="/unit-conversions/new" element={<ProtectedRoute roles={['admin', 'editor', 'approver']}><UnitConversionFormPage /></ProtectedRoute>} />
      <Route path="/unit-conversions/:id/edit" element={<ProtectedRoute roles={['admin', 'editor', 'approver']}><UnitConversionFormPage /></ProtectedRoute>} />
      <Route path="/unit-conversions/:id" element={<ProtectedRoute><UnitConversionDetailPage /></ProtectedRoute>} />
      <Route path="/lca-impact-factors" element={<ProtectedRoute roles={['admin', 'editor', 'approver']}><LcaImpactFactorsPage /></ProtectedRoute>} />
      <Route path="/lca-impact-factors/live/:impactId" element={<ProtectedRoute roles={['admin', 'editor', 'approver']}><LcaImpactFactorDetailPage /></ProtectedRoute>} />
      <Route path="/lca-impact-factors/:id" element={<ProtectedRoute roles={['admin', 'editor', 'approver']}><LcaImpactFactorDetailPage /></ProtectedRoute>} />
      <Route path="/audit-logs" element={<ProtectedRoute><AuditLogsPage /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute roles={['admin']}><UserManagementPage /></ProtectedRoute>} />
      <Route path="/esg-report-extraction" element={<ProtectedRoute roles={['admin', 'editor', 'approver']}><EsgReportExtractionPage /></ProtectedRoute>} />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
